import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { loginMember } from '../api/memberAuth';
import {
    AUTH_MODE,
    AUTH_SIMULATION,
    assertSimulatedCredentials,
    buildSimulatedUser,
    waitSimulatedLogin,
} from '../config/authSimulation';

const AuthContext = createContext(null);

/**
 * @param {string | null} raw
 * @returns {object | null}
 */
function parseStoredUser(raw) {
    if (!raw) return null;
    try {
        const u = JSON.parse(raw);
        if (u && typeof u === 'object' && u.expiresAt && !u.simulatedSession) {
            const exp = new Date(u.expiresAt);
            if (!Number.isNaN(exp.getTime()) && exp <= new Date()) {
                return null;
            }
        }
        return u;
    } catch {
        return null;
    }
}

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [ready, setReady] = useState(false);

    useEffect(() => {
        try {
            const raw = localStorage.getItem(AUTH_SIMULATION.storageKey);
            const parsed = parseStoredUser(raw);
            if (parsed) {
                setUser(parsed);
            } else if (raw) {
                localStorage.removeItem(AUTH_SIMULATION.storageKey);
            }
        } catch {
            localStorage.removeItem(AUTH_SIMULATION.storageKey);
        }
        setReady(true);
    }, []);

    const login = useCallback(async (email, password) => {
        if (AUTH_MODE === 'simulation') {
            assertSimulatedCredentials(email, password);
            await waitSimulatedLogin();
            const nextUser = buildSimulatedUser(email);
            localStorage.setItem(
                AUTH_SIMULATION.storageKey,
                JSON.stringify(nextUser),
            );
            setUser(nextUser);
            return nextUser;
        }

        const nextUser = await loginMember(email, password);
        localStorage.setItem(
            AUTH_SIMULATION.storageKey,
            JSON.stringify(nextUser),
        );
        setUser(nextUser);
        return nextUser;
    }, []);

    const logout = useCallback(() => {
        localStorage.removeItem(AUTH_SIMULATION.storageKey);
        setUser(null);
    }, []);

    const updateUser = useCallback((patch) => {
        setUser((prev) => {
            if (!prev) return prev;
            const next = { ...prev, ...patch };
            localStorage.setItem(
                AUTH_SIMULATION.storageKey,
                JSON.stringify(next),
            );
            return next;
        });
    }, []);

    const value = useMemo(
        () => ({
            user,
            authToken: user?.token ?? null,
            ready,
            authMode: AUTH_MODE,
            isSimulatedAuth: AUTH_MODE === 'simulation',
            isAuthenticated: Boolean(user),
            login,
            logout,
            updateUser,
        }),
        [user, ready, login, logout, updateUser],
    );

    return (
        <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) {
        throw new Error('useAuth yalnızca AuthProvider içinde kullanılabilir');
    }
    return ctx;
}
